import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Scroll, 
  Upload, 
  Users, 
  Calendar, 
  FileText, 
  CheckCircle, 
  Clock,
  ChevronRight,
  Utensils
} from 'lucide-react';
import { toast } from 'sonner';

const DashboardPage = () => {
  const { user, api, canEdit } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrator',
      office: 'Büro',
      service: 'Service',
      kitchen: 'Küche'
    };
    return labels[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-burgundy text-white',
      office: 'bg-gold text-charcoal-900',
      service: 'bg-emerald-600 text-white',
      kitchen: 'bg-orange-500 text-white'
    };
    return colors[role] || 'bg-stone-500 text-white';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-burgundy">Wird geladen...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-burgundy">
            Willkommen, {user?.name}
          </h1>
          <div className="flex items-center gap-2 mt-2">
            <Badge className={getRoleBadgeColor(user?.role)}>
              {getRoleLabel(user?.role)}
            </Badge>
            <span className="text-charcoal-500 text-sm">
              VA-Facts Manager
            </span>
          </div>
        </div>
        
        {canEdit() && (
          <Link to="/upload">
            <Button 
              className="bg-burgundy hover:bg-burgundy-800 text-white font-serif gap-2"
              data-testid="upload-btn"
            >
              <Upload className="h-4 w-4" />
              PDF hochladen
            </Button>
          </Link>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-burgundy shadow-card hover:shadow-floating transition-shadow duration-300" data-testid="stat-total">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-charcoal-600">Gesamt Sheets</CardTitle>
            <FileText className="h-5 w-5 text-burgundy" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-burgundy font-serif">{stats?.total_sheets || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500 shadow-card hover:shadow-floating transition-shadow duration-300" data-testid="stat-published">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-charcoal-600">Veröffentlicht</CardTitle>
            <CheckCircle className="h-5 w-5 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600 font-serif">{stats?.published_sheets || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-gold shadow-card hover:shadow-floating transition-shadow duration-300" data-testid="stat-draft">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-charcoal-600">Entwürfe</CardTitle>
            <Clock className="h-5 w-5 text-gold" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gold-600 font-serif">{stats?.draft_sheets || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500 shadow-card hover:shadow-floating transition-shadow duration-300" data-testid="stat-orders">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-charcoal-600">Aufträge</CardTitle>
            <Scroll className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600 font-serif">{stats?.total_orders || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Upcoming Events */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card className="shadow-card" data-testid="quick-actions">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-burgundy flex items-center gap-2">
              <Utensils className="h-5 w-5" />
              Schnellzugriff
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/sheets" className="block">
              <Button 
                variant="outline" 
                className="w-full justify-between border-stone-300 hover:border-burgundy hover:text-burgundy"
                data-testid="quick-view-sheets"
              >
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Function Sheets
                </span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </Link>
            
            {canEdit() && (
              <>
                <Link to="/upload" className="block">
                  <Button 
                    variant="outline" 
                    className="w-full justify-between border-stone-300 hover:border-burgundy hover:text-burgundy"
                    data-testid="quick-upload"
                  >
                    <span className="flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      PDF hochladen
                    </span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/history" className="block">
                  <Button 
                    variant="outline" 
                    className="w-full justify-between border-stone-300 hover:border-burgundy hover:text-burgundy"
                    data-testid="quick-history"
                  >
                    <span className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Historie
                    </span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </Link>
              </>
            )}
            
            {user?.role === 'admin' && (
              <Link to="/users" className="block">
                <Button 
                  variant="outline" 
                  className="w-full justify-between border-stone-300 hover:border-burgundy hover:text-burgundy"
                  data-testid="quick-users"
                >
                  <span className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Benutzerverwaltung
                  </span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card className="lg:col-span-2 shadow-card" data-testid="upcoming-events">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-burgundy flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Aktuelle Veranstaltungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.upcoming_events?.length > 0 ? (
              <div className="space-y-4">
                {stats.upcoming_events.map((event, index) => (
                  <Link 
                    key={event.id} 
                    to={`/sheets/${event.id}`}
                    className="block"
                  >
                    <div 
                      className="flex items-center justify-between p-4 rounded-lg bg-stone-50 hover:bg-stone-100 transition-colors duration-200 border border-stone-200 hover:border-gold"
                      style={{ animationDelay: `${index * 100}ms` }}
                      data-testid={`event-${event.id}`}
                    >
                      <div className="flex-1">
                        <h3 className="font-semibold text-charcoal-800">{event.title || 'Unbenannt'}</h3>
                        <div className="flex items-center gap-4 text-sm text-charcoal-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {event.date || '-'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {event.time || '-'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {event.guest_count || '-'} Gäste
                          </span>
                        </div>
                      </div>
                      <Badge 
                        className={event.status === 'published' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-gold-100 text-gold-700'
                        }
                      >
                        {event.status === 'published' ? 'Veröffentlicht' : 'Entwurf'}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-charcoal-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-stone-300" />
                <p>Noch keine Veranstaltungen vorhanden</p>
                {canEdit() && (
                  <Link to="/upload">
                    <Button className="mt-4 bg-burgundy hover:bg-burgundy-800 text-white">
                      Erste Auftragsbestätigung hochladen
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
